import { DateTime } from "luxon";
import type { EventOccurrence } from "@/api/clubsApi";

export interface EventMonthGroup {
  monthLabel: string;
  month: number;
  year: number;
  events: EventOccurrence[];
}

/**
 * Groups events by month.
 * When reverse is false (default): groups and events sorted oldest-first.
 * When reverse is true: groups and events sorted newest-first (for past events).
 * If the year differs from the current year, includes the year in the label.
 */
export function groupEventsByMonth(
  events: EventOccurrence[],
  options?: { reverse?: boolean }
): EventMonthGroup[] {
  const reverse = options?.reverse ?? false;
  const currentYear = DateTime.now().year;
  const groupMap = new Map<string, EventMonthGroup>();

  for (const event of events) {
    const dt = DateTime.fromISO(event.occurrence_start);
    const key = `${dt.year}-${dt.month}`;

    if (!groupMap.has(key)) {
      const label =
        dt.year !== currentYear
          ? dt.toFormat("MMMM yyyy")
          : dt.toFormat("MMMM");

      groupMap.set(key, {
        monthLabel: label,
        month: dt.month,
        year: dt.year,
        events: [],
      });
    }

    groupMap.get(key)!.events.push(event);
  }

  const dir = reverse ? -1 : 1;

  // Sort groups
  const groups = Array.from(groupMap.values()).sort((a, b) => {
    if (a.year !== b.year) return (a.year - b.year) * dir;
    return (a.month - b.month) * dir;
  });

  // Sort events within each group
  for (const group of groups) {
    group.events.sort(
      (a, b) =>
        (new Date(a.occurrence_start).getTime() -
          new Date(b.occurrence_start).getTime()) *
        dir
    );
  }

  return groups;
}
